import { useQuery } from "@tanstack/react-query";
import { Plus, TrendingUp } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Select,
  StatusBadge,
  cn,
} from "../../shared/ui";
import type { SprintSummary, VelocityView } from "../../shared/types/api";
import { CreateSprintDrawer } from "./CreateSprintDrawer";
import { VelocityChart } from "./VelocityChart";
import {
  SPRINT_STATUS_LABELS,
  SPRINT_STATUS_TONE,
  formatDaysRemaining,
  formatHours,
  formatPercent,
} from "./sprintVocabulary";
import { usePublishCurrentProject } from "../../shared/hooks/currentProject";
import { useProjectSelection } from "./useProjectSelection";

/**
 * Sprints for a project, with the velocity trend they add up to (§48, §102).
 *
 * <p>The trend sits above the list rather than on a separate screen, because the two questions are
 * asked together: planning the next sprint means knowing what the last few actually delivered.
 */
export function SprintsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const { projectId, select, projects, loading } = useProjectSelection();
  const [creating, setCreating] = useState(false);

  usePublishCurrentProject(
    projectId,
    projects.find((project) => project.id === projectId)?.name,
  );

  const sprints = useQuery({
    queryKey: ["sprints", projectId],
    queryFn: () => api.get<SprintSummary[]>(`/api/v1/sprints?projectId=${projectId}`),
    enabled: Boolean(projectId),
  });

  const velocity = useQuery({
    queryKey: ["velocity", projectId],
    queryFn: () => api.get<VelocityView>(`/api/v1/projects/${projectId}/velocity`),
    enabled: Boolean(projectId),
  });

  if (loading) {
    return <LoadingState label="Loading projects…" />;
  }
  if (projects.length === 0) {
    return (
      <Card>
        <EmptyState
          title="No projects yet"
          message="A sprint belongs to a project. Create one first."
          action={<Button onClick={() => navigate("/projects")}>Go to projects</Button>}
        />
      </Card>
    );
  }

  const rows = sprints.data ?? [];

  return (
    <>
      <PageHeader
        title="Sprints"
        subtitle="What the team committed to, and what it delivered."
        actions={
          <div className="flex items-center gap-2">
            <div className="w-56">
              <Select value={projectId} onChange={(event) => select(event.target.value)}>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </Select>
            </div>
            {can("SPRINT_CREATE") && (
              <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
                New sprint
              </Button>
            )}
          </div>
        }
      />

      {velocity.data && velocity.data.sprints.length > 0 && (
        <Card className="mb-4">
          <CardHeader
            title="Velocity"
            subtitle={
              <span className="flex flex-wrap items-center gap-3">
                <span>
                  Recent average{" "}
                  <span className="tnum font-medium text-ink-900">
                    {Math.round(velocity.data.recentVelocity)}
                  </span>{" "}
                  points
                </span>
                <span className="text-ink-400">·</span>
                <span>
                  Delivers{" "}
                  <span className="tnum font-medium text-ink-900">
                    {formatPercent(velocity.data.averageCommitmentRatio)}
                  </span>{" "}
                  of what it commits
                </span>
              </span>
            }
            action={<TrendingUp className="h-4 w-4 text-ink-400" />}
          />
          <CardBody>
            <VelocityChart data={velocity.data} />
            <p className="mt-3 text-xs text-ink-400">
              A team measure, never a personal one. Sprint metrics are recorded without
              attribution, so no per-person score can be derived from them.
            </p>
          </CardBody>
        </Card>
      )}

      {sprints.isError ? (
        <ErrorState error={sprints.error} onRetry={() => void sprints.refetch()} />
      ) : sprints.isPending ? (
        <LoadingState label="Loading sprints…" />
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            title="No sprints yet"
            message="A sprint is a time box with a goal and a commitment. Create one, pull work in from the backlog, then start it — starting is what records what was committed."
            action={
              can("SPRINT_CREATE") && (
                <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
                  Create the first sprint
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((sprint) => (
            <Card
              key={sprint.id}
              className={cn(
                "cursor-pointer transition-colors hover:border-ink-300",
                sprint.status === "ACTIVE" && "border-emerald-300 ring-1 ring-emerald-200",
              )}
            >
              <button
                type="button"
                onClick={() => navigate(`/sprints/${sprint.id}`)}
                className="w-full p-5 text-left"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-medium text-ink-900">{sprint.name}</h3>
                      <StatusBadge tone={SPRINT_STATUS_TONE[sprint.status]}>
                        {SPRINT_STATUS_LABELS[sprint.status]}
                      </StatusBadge>
                      {sprint.overrunning && (
                        <StatusBadge tone="red">Past its end date</StatusBadge>
                      )}
                      {sprint.teamName && (
                        <span className="text-xs text-ink-500">{sprint.teamName}</span>
                      )}
                    </div>
                    {sprint.goal ? (
                      <p className="mt-0.5 text-sm text-ink-500">{sprint.goal}</p>
                    ) : (
                      <p className="mt-0.5 text-sm text-ink-400">
                        No goal set — a sprint without one has nothing to be reviewed against.
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 gap-6 text-right">
                    <Figure label="Dates" value={`${sprint.startDate} → ${sprint.endDate}`} />
                    <Figure label="Work" value={`${sprint.itemCount} items`} />
                    <Figure label="Points" value={String(sprint.storyPoints)} />
                    <Figure
                      label="Capacity"
                      value={formatHours(sprint.capacityHours)}
                    />
                    <Figure
                      label="Remaining"
                      value={formatDaysRemaining(sprint.daysRemaining, sprint.status)}
                    />
                  </div>
                </div>
              </button>
            </Card>
          ))}
        </div>
      )}

      <CreateSprintDrawer
        projectId={projectId}
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={(sprintId) => {
          void sprints.refetch();
          if (sprintId) {
            navigate(`/sprints/${sprintId}`);
          }
        }}
      />
    </>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-ink-500">{label}</p>
      <p className="tnum mt-0.5 text-sm font-medium text-ink-900">{value}</p>
    </div>
  );
}
